package se.mittpass.shared.repository

import io.ktor.client.*
import io.ktor.client.call.body
import io.ktor.client.request.*
import io.ktor.http.*
import se.mittpass.shared.api.AuthResponse
import se.mittpass.shared.api.LoginRequest
import se.mittpass.shared.api.RegisterRequest
import se.mittpass.shared.auth.AuthStorage

class AuthRepository(
    private val httpClient: HttpClient,
    private val authStorage: AuthStorage
) {
    // Returns null token when email verification is required (HTTP 201 with message).
    suspend fun register(req: RegisterRequest): Result<AuthResponse?> = runCatching {
        val response = httpClient.post("auth/register") { setBody(req) }
        if (!response.status.isSuccess()) {
            val body = runCatching { response.body<Map<String, String>>() }.getOrNull()
            val msg = body?.get("error") ?: "Registrering misslyckades."
            throw Exception(msg)
        }
        if (response.status == HttpStatusCode.Created) {
            // Email verification pending — no token issued yet
            null
        } else {
            response.body<AuthResponse>().also {
                authStorage.setToken(it.token)
                it.refreshToken?.let { rt -> authStorage.setRefreshToken(rt) }
            }
        }
    }

    suspend fun login(req: LoginRequest): Result<AuthResponse> = runCatching {
        val response = httpClient.post("auth/login") { setBody(req) }
        if (!response.status.isSuccess()) {
            val body = runCatching { response.body<Map<String, String>>() }.getOrNull()
            val msg = body?.get("error") ?: when (response.status) {
                HttpStatusCode.Forbidden -> "Verifiera din e-postadress."
                else -> "Felaktig e-post eller lösenord."
            }
            throw Exception(msg)
        }
        response.body<AuthResponse>().also {
            authStorage.setToken(it.token)
            it.refreshToken?.let { rt -> authStorage.setRefreshToken(rt) }
        }
    }

    suspend fun verifyEmail(token: String): Result<Unit> = runCatching {
        httpClient.get("auth/verify-email") { parameter("token", token) }
    }

    suspend fun resendVerification(email: String): Result<Unit> = runCatching {
        httpClient.post("auth/resend-verification") {
            contentType(ContentType.Application.Json)
            setBody(mapOf("email" to email))
        }
    }

    suspend fun forgotPassword(email: String): Result<Unit> = runCatching {
        httpClient.post("auth/forgot-password") {
            contentType(ContentType.Application.Json)
            setBody(mapOf("email" to email))
        }
    }

    suspend fun resetPassword(token: String, newPassword: String): Result<Unit> = runCatching {
        httpClient.post("auth/reset-password") {
            contentType(ContentType.Application.Json)
            setBody(mapOf("token" to token, "newPassword" to newPassword))
        }
    }

    suspend fun changePassword(currentPassword: String, newPassword: String): Result<Unit> = runCatching {
        httpClient.post("auth/change-password") {
            contentType(ContentType.Application.Json)
            setBody(mapOf("currentPassword" to currentPassword, "newPassword" to newPassword))
        }
    }

    fun logout() {
        authStorage.clear()
    }

    fun isLoggedIn(): Boolean = authStorage.getToken() != null
}
